import {Component, inject, OnInit}                        from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {MatSnackBar}                                      from '@angular/material/snack-bar';
import {ActivatedRoute, Router}                           from '@angular/router';
import {AuthService}                                      from '../../services/auth.service';
import {PaymentService}                                   from '@src/app/core/services/payment.service';

@Component({
    standalone: false,
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit
{
    private readonly payments = inject(PaymentService);
    private readonly publicRedirectPrefixes: string[] = [
        '/forgot-password',
        '/reset-password',
        '/sign-up',
        '/create-password',
        '/code-verification',
    ];

    public loginForm: UntypedFormGroup;
    public error: any;
    public submitted = false;

    constructor(
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private fb: UntypedFormBuilder,
        private authService: AuthService,
        private snackBar: MatSnackBar,
    )
    {
        if (this.authService.authenticated) {
            this.router.navigate(['/app']).then(r => r);
        }
    }

    get f()
    {
        return this.loginForm.controls;
    }

    public ngOnInit(): void
    {
        this.loginForm = this.fb.group({
            email: [null, [Validators.required, Validators.email]],
            password: [null, Validators.required]
        });

        const publicRedirectUrl = this.resolvePublicRedirectUrl(this.activatedRoute.snapshot.queryParamMap.get('redirectUrl'));

        if (publicRedirectUrl) {
            this.router.navigateByUrl(publicRedirectUrl).then(r => r);
            return;
        }
    }

    public onSubmit(): void
    {
        this.submitted = true;

        if (this.loginForm.invalid) {
            return;
        }

        this.loginForm.disable();

        this.authService.login(this.loginForm.value).subscribe(
            response => {
                const redirectUrl = this.activatedRoute.snapshot.queryParamMap.get('redirectUrl') || '/signed-in-redirect';

                this.snackBar.open('Signed in successfully.', 'Ok', {duration: 2000});

                if (redirectUrl === '/signed-in-redirect' && ['view_settings', 'add_credits'].every(permission => response.data?.user?.permissions?.includes(permission))) {
                    this.payments.getPlan().subscribe({
                        next: state => this.router.navigateByUrl(state.checkout_required === true && !state.billing_managed_externally
                            ? '/app/settings/7' : redirectUrl),
                        error: () => this.router.navigateByUrl(redirectUrl),
                    });

                    return;
                }

                this.router.navigateByUrl(redirectUrl).then(r => r);
            },
            (response: any) => {
                this.loginForm.enable();
                this.error = response;

                this.snackBar.open(response?.error?.message || 'Unable to sign in. Please try again.', 'Ok');
            }
        );
    }

    private resolvePublicRedirectUrl(redirectUrl: string | null): string | null
    {
        if (!redirectUrl) {
            return null;
        }

        const decodedRedirectUrl = this.decodeRedirectUrl(redirectUrl);
        const candidates = [decodedRedirectUrl];

        if (decodedRedirectUrl.includes('#')) {
            const fragment = decodedRedirectUrl.slice(decodedRedirectUrl.indexOf('#') + 1);
            candidates.unshift(fragment);
        }

        for (const candidate of candidates) {
            const normalizedCandidate = this.normalizeRedirectCandidate(candidate);

            if (this.publicRedirectPrefixes.some(prefix => normalizedCandidate.startsWith(prefix))) {
                return normalizedCandidate;
            }
        }

        return null;
    }

    private decodeRedirectUrl(redirectUrl: string): string
    {
        try {
            return decodeURIComponent(redirectUrl);
        } catch {
            return redirectUrl;
        }
    }

    private normalizeRedirectCandidate(candidate: string): string
    {
        const trimmedCandidate = candidate.trim();

        if (!trimmedCandidate) {
            return trimmedCandidate;
        }

        return trimmedCandidate.startsWith('/') ? trimmedCandidate : `/${trimmedCandidate}`;
    }
}
